"use client";

import dynamic from "next/dynamic";
import { Component, useEffect, useState, type ReactNode } from "react";
import { COLORS } from "@/lib/constants";
import { afterSplash, splashPlayed } from "@/lib/splash";

/** three and postprocessing download only when a splash is actually playing. */
const PixelBlast = dynamic(() => import("@/components/ui/PixelBlast"), { ssr: false });

/** Covers the splash's exit (columns 0.55s plus their stagger) before WebGL is freed. */
const EXIT_MS = 900;

const hasWebGL = () => {
  try {
    const canvas = document.createElement("canvas");
    return Boolean(canvas.getContext("webgl2") ?? canvas.getContext("webgl"));
  } catch {
    return false;
  }
};

/** A WebGL failure after the check costs the splash its pixels, never the page. */
class Fallback extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * React Bits' Pixel Blast in Mint behind the splash logo. An extra the splash never waits
 * for: it mounts a frame after hydration, fades in once its code has arrived, and the
 * splash still lifts on its own clock. Nothing mounts when the splash is not playing
 * (repeat visits, reduced motion, no JavaScript) or without WebGL, where the splash is the
 * plain white it always was, and WebGL is released once the splash has gone.
 */
export const SplashPixels = () => {
  const [on, setOn] = useState(false);

  useEffect(() => {
    if (!splashPlayed() || document.documentElement.hasAttribute("data-splash-out") || !hasWebGL()) return;
    const frame = requestAnimationFrame(() => setOn(true));
    let timer = 0;
    const cleanup = afterSplash(() => {
      timer = window.setTimeout(() => setOn(false), EXIT_MS);
    });
    return () => {
      cancelAnimationFrame(frame);
      cleanup();
      window.clearTimeout(timer);
    };
  }, []);

  if (!on) return null;
  return (
    <div aria-hidden className="splash-pixels">
      <Fallback>
        <PixelBlast
          variant="square"
          color={COLORS.MINT}
          pixelSize={4}
          patternScale={2.5}
          patternDensity={0.9}
          edgeFade={0.3}
          speed={0.6}
          enableRipples={false}
          antialias={false}
          transparent
        />
      </Fallback>
    </div>
  );
};
